// Control centre home (Business UI handoff H42, 2026-10-07): "All systems.
// One clear view." — catch the exceptions, keep the business moving.
//
// Every number is a real read from an existing endpoint:
//   paid website orders waiting   GET /website-orders?status=paid
//   new website requests          GET /website-requests?status=new
//   catalogue readiness + photos  GET /ingestor/health
// A source that fails says so in its own tile; nothing is ever shown as 0
// because a request failed.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

interface HealthRow { listings: number; ready: number; price: number; image: number; description: number; category: number; colour: number; offer: number }
interface Health { measuredAt: string | null; totals: HealthRow; photos: { checked: number; broken: number; small: number; lastCheckedAt: string | null } }

type Load<T> = { state: "loading" } | { state: "error" } | { state: "ok"; data: T };

function useLoad<T>(path: string, pick: (r: unknown) => T): Load<T> {
  const [s, setS] = useState<Load<T>>({ state: "loading" });
  useEffect(() => {
    let live = true;
    api<unknown>(path)
      .then((r) => { if (live) setS({ state: "ok", data: pick(r) }); })
      .catch(() => { if (live) setS({ state: "error" }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  return s;
}

const n = (x: number) => x.toLocaleString("en-US");
const WORKFLOW = typeof window !== "undefined" && window.location.hostname.endsWith("withother.com") ? "https://flow.withother.com" : "https://app.getmerchd.com";
const PORTAL = typeof window !== "undefined" && window.location.hostname.endsWith("withother.com") ? "https://client.withother.com" : "https://client.getmerchd.com";

function Metric({ label, load, value, note, to }: { label: string; load: Load<unknown>; value: string; note: string; to: string }) {
  return (
    <Link to={to} className="flex flex-col bg-white p-5 transition-colors hover:bg-[#fafaf8]">
      <span className="text-xs text-ink-muted">{label}</span>
      {load.state === "loading" ? (
        <span className="mt-2 h-8 w-20 animate-pulse rounded bg-paper" aria-label="Loading" />
      ) : load.state === "error" ? (
        <span className="mt-1.5 text-sm text-risk">Couldn't load</span>
      ) : (
        <span className="mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] text-ink tabular-nums">{value}</span>
      )}
      <span className="mt-1.5 text-xs text-ink-muted">{note}</span>
    </Link>
  );
}

export function HomePage() {
  const { user } = useAuth();
  const orders = useLoad("/website-orders?status=paid", (r) => ((r as { orders?: unknown[] }).orders ?? []).length);
  const requests = useLoad("/website-requests?status=new", (r) => ((r as { requests?: unknown[] }).requests ?? []).length);
  const health = useLoad("/ingestor/health", (r) => r as Health);

  const h = health.state === "ok" ? health.data : null;
  const readyPct = h && h.totals.listings ? Math.round((h.totals.ready / h.totals.listings) * 100) : 0;
  const photoIssues = h ? h.photos.broken + h.photos.small : 0;

  const attention: Array<{ title: string; detail: string; badge: string; action: string; to: string }> = [];
  if (orders.state === "ok" && orders.data > 0) attention.push({ title: `${orders.data === 1 ? "A paid order needs" : `${orders.data} paid orders need`} a workflow record.`, detail: "Paid on the website · waiting to be created in the workflow", badge: "Action needed", action: "Inspect handoff", to: "/website-orders" });
  if (requests.state === "ok" && requests.data > 0) attention.push({ title: `${requests.data === 1 ? "A website request is" : `${requests.data} website requests are`} waiting.`, detail: "Customers asked for a price or sent a message", badge: "New", action: "Review requests", to: "/website-orders" });
  if (h && photoIssues > 0) attention.push({ title: `${n(photoIssues)} product photos need a look.`, detail: `${n(h.photos.broken)} broken · ${n(h.photos.small)} too small`, badge: "Review", action: "Review photos", to: "/studio" });
  const failed = [orders, requests, health].some((x) => x.state === "error");

  const gaps: Array<[string, number]> = h
    ? [["No price", h.totals.price], ["No photo", h.totals.image], ["No description", h.totals.description], ["No shelf", h.totals.category], ["No colours", h.totals.colour], ["No live supplier listing", h.totals.offer]]
    : [];

  return (
    <div className="mx-auto w-full max-w-[1500px] p-8">
      <header className="mb-7 mt-2 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-ink-muted">Control centre / Overview</p>
          <h1 className="mb-2.5 mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">All systems. One clear view.</h1>
          <p className="text-[14px] text-ink-muted">Catch the exceptions. Keep the business moving{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>
        </div>
        <Link to="/website-orders" className="inline-flex min-h-[40px] items-center rounded bg-ink px-[14px] text-[14px] text-white hover:bg-ink/90">Review handoffs</Link>
      </header>

      <section aria-label="Key numbers" className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Orders needing handoff" load={orders} value={orders.state === "ok" ? n(orders.data) : ""} note="Paid on the website" to="/website-orders" />
        <Metric label="Requests waiting" load={requests} value={requests.state === "ok" ? n(requests.data) : ""} note="Quote requests and messages" to="/website-orders" />
        <Metric label="Catalogue ready to sell" load={health} value={h ? `${readyPct}%` : ""} note={h ? `${n(h.totals.ready)} of ${n(h.totals.listings)} listings` : "Listings that pass every check"} to="/studio" />
        <Metric label="Photos to review" load={health} value={h ? n(photoIssues) : ""} note={h?.photos.lastCheckedAt ? `Checked ${new Date(h.photos.lastCheckedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "Broken or too small"} to="/studio" />
      </section>

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,1fr)]">
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px]">Needs attention.</h2>
          {attention.length === 0 ? (
            <p className="text-[14px] text-ink-muted">{failed ? "Some sources didn't load — see the tiles above." : "Nothing needs you right now."}</p>
          ) : (
            <ul>
              {attention.map((a) => (
                <li key={a.title} className="flex items-center justify-between gap-4 border-b border-[#e8e8e5] py-4 first:pt-0 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium">{a.title} <span className="ml-1 inline-block rounded bg-warn-bg px-2 py-0.5 text-[11px] text-warn">{a.badge}</span></p>
                    <p className="mt-1 text-xs text-ink-muted">{a.detail}</p>
                  </div>
                  <Link to={a.to} className="inline-flex min-h-[40px] shrink-0 items-center rounded border border-rule bg-white px-3.5 text-[13px] hover:bg-paper">{a.action}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px]">System ownership</h2>
          <dl className="space-y-4">
            <div><dt className="font-semibold"><a href={WORKFLOW} className="hover:underline" target="_blank" rel="noreferrer">Flow ↗</a></dt><dd className="text-xs text-ink-muted">Clients, projects, orders and financial records</dd></div>
            <div><dt className="font-semibold">Control centre</dt><dd className="text-xs text-ink-muted">Website orders, catalogue, integrations and reconciliation</dd></div>
            <div><dt className="font-semibold"><a href={PORTAL} className="hover:underline" target="_blank" rel="noreferrer">Client portal ↗</a></dt><dd className="text-xs text-ink-muted">Customer access to their own orders, invoices and assets</dd></div>
          </dl>
        </section>
      </div>

      <section className="mt-6">
        <div className="mb-3.5 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-[-0.4px]">Catalogue readiness</h2>
          <span className="text-xs text-ink-muted">{h?.measuredAt ? `Measured ${new Date(h.measuredAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}</span>
        </div>
        <div className="overflow-x-auto rounded-md border border-rule bg-white">
          <table className="w-full whitespace-nowrap text-left">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-[11px] text-ink-muted">
              <tr><th scope="col" className="px-[18px] py-[15px] font-medium">What stops a listing selling</th><th scope="col" className="px-[18px] py-[15px] text-right font-medium">Listings</th><th scope="col" className="px-[18px] py-[15px] font-medium">Next step</th></tr>
            </thead>
            <tbody className="text-[13px]">
              {health.state === "loading" ? (
                <tr><td colSpan={3} className="px-[18px] py-[15px] text-ink-muted">Loading catalogue health…</td></tr>
              ) : health.state === "error" ? (
                <tr><td colSpan={3} className="px-[18px] py-[15px] text-risk" role="alert">Catalogue health didn't load.</td></tr>
              ) : (
                gaps.map(([label, count]) => (
                  <tr key={label} className="border-b border-[#e7e7e4] last:border-0">
                    <td className="px-[18px] py-[15px]">{label}</td>
                    <td className="px-[18px] py-[15px] text-right tabular-nums">{n(count)}</td>
                    <td className="px-[18px] py-[15px]"><Link to="/studio" className="underline underline-offset-4 hover:text-ink-muted">Open Studio</Link></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
